import type { ItemCategory, ItemMeta, TextureCatalogEntry, TextureId } from '@/types/domain';
import { textureCatalog } from './TextureCatalog';

const GENERIC_FALLBACKS: TextureId[] = [
  'minecraft:crafting_table',
  'minecraft:stick',
  'minecraft:iron_ingot',
  'minecraft:oak_planks',
  'minecraft:stone',
  'minecraft:chest',
];

function scoreEntry(entry: TextureCatalogEntry, ingredientTags: string[], categories: string[], materials: string[]): number {
  let score = 0;
  for (const t of ingredientTags) if (entry.tags.includes(t)) score += 2;
  for (const c of categories) if (entry.category === (c as ItemCategory)) score += 1.5;
  for (const m of materials) if (entry.tags.includes(m)) score += 1.5;
  // small boost for item-source icons (cleaner single items)
  if (entry.source === 'item') score += 0.25;
  return score;
}

export interface RetrieveOptions {
  /** Force-include this texture id (e.g. a locked texture). */
  lockTextureId?: TextureId;
  /** Prefer entries of this category. */
  lockCategory?: ItemCategory;
  /** Max candidates to return (20-50 per spec). */
  limit?: number;
}

export class TextureCandidateRetriever {
  /**
   * Given the ingredient metas, return a compact list of likely texture candidates
   * the model may reuse. The model must select one of these (or a validated id).
   */
  retrieve(ingredients: ItemMeta[], opts: RetrieveOptions = {}): TextureCatalogEntry[] {
    const limit = Math.min(Math.max(opts.limit ?? 40, 10), 50);
    const ingredientTags = new Set<string>();
    const categories = new Set<string>();
    const materials = new Set<string>();
    for (const ing of ingredients) {
      ing.tags.forEach((t) => ingredientTags.add(t));
      categories.add(ing.category);
      if (ing.material) materials.add(ing.material);
      if (ing.toolType) ingredientTags.add(ing.toolType);
    }
    if (opts.lockCategory) categories.add(opts.lockCategory);

    const scored: Array<{ e: TextureCatalogEntry; s: number }> = [];
    for (const e of textureCatalog.all()) {
      if (e.category === 'mob') continue; // spawn eggs rarely good item icons
      const s = scoreEntry(e, [...ingredientTags], [...categories], [...materials]);
      if (s > 0) scored.push({ e, s });
    }
    scored.sort((a, b) => b.s - a.s);

    const seen = new Set<TextureId>();
    const out: TextureCatalogEntry[] = [];

    const add = (id: TextureId) => {
      if (seen.has(id)) return;
      const e = textureCatalog.get(id);
      if (e) {
        seen.add(id);
        out.push(e);
      }
    };

    // ingredient textures first (so model can reuse an ingredient)
    for (const ing of ingredients) add(ing.textureId);
    // top scored
    for (const { e } of scored) {
      add(e.id);
      if (out.length >= limit) break;
    }
    // ensure generic fallbacks present
    for (const id of GENERIC_FALLBACKS) {
      if (out.length >= limit) break;
      add(id);
    }
    // locked texture must be available to the model
    if (opts.lockTextureId) add(opts.lockTextureId);

    return out.slice(0, Math.max(limit, out.length));
  }
}

export const textureCandidateRetriever = new TextureCandidateRetriever();
